// services/ProviderService.js - ReactJS (web)
import api from '../api/api';

class ProviderService {
  // ==================== PUBLIC ROUTES ====================
  // No authentication required - accessible by customers/clients without account

  /**
   * Get all service providers with filtering, sorting, and pagination
   * GET /api/providers
   */
// ==================== GET ALL PROVIDERS ====================
// Returns ALL providers in RANDOM order by default

async getAllProviders(params = {}) {
  try {
    const requestParams = {
      ...params,

      // Force random ordering
      sortBy: 'random',

      // Get all providers (backend caps this at 100 unless changed)
      limit: 100,

      // Always start from first page
      page: 1
    };

    console.log(
      '🎲 GET ALL PROVIDERS - RANDOM:',
      requestParams
    );

    const response = await api.get('/providers', {
      params: requestParams
    });

    console.log(
      '🎲 RANDOM PROVIDERS RESPONSE:',
      response.data
    );

    return response.data;

  } catch (error) {
    console.error(
      '❌ Failed to get random providers:',
      error
    );

    throw this.handleError(error);
  }
}

  /**
   * Get service provider by ID
   * GET /api/providers/:id
   */
  async getProviderById(id, populate = null) {
    try {
      const params = populate ? { populate } : {};
      const response = await api.get(`/providers/${id}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get service providers by service type
   * GET /api/providers/type/:serviceType
   */
  async getProvidersByServiceType(serviceType, page = 1, limit = 10) {
    try {
      const response = await api.get(`/providers/type/${serviceType}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get featured service providers
   * GET /api/providers/featured
   */
  async getFeaturedProviders(page = 1, limit = 10) {
    try {
      const response = await api.get('/providers/featured', {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get service providers with random sorting
   * GET /api/providers/random
   */
  async getRandomProviders(page = 1, limit = 10, filter = {}) {
    try {
      const params = { page, limit };
      if (Object.keys(filter).length > 0) {
        params.filter = JSON.stringify(filter);
      }
      const response = await api.get('/providers/random', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get nearby providers
   * GET /api/providers/nearby
   */
  async getNearbyProviders(latitude, longitude, maxDistance = 10, page = 1, limit = 10, filters = {}) {
    try {
      const params = {
        latitude,
        longitude,
        maxDistance,
        page,
        limit,
        ...filters
      };
      const response = await api.get('/providers/nearby', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get nearby providers from request (IP-based)
   * GET /api/providers/nearby-me
   */
  async getNearbyProvidersFromRequest(maxDistance = 10, page = 1, limit = 10, filters = {}) {
    try {
      const params = {
        maxDistance,
        page,
        limit,
        ...filters
      };
      const response = await api.get('/providers/nearby-me', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by city
   * GET /api/providers/city/:city
   */
  async getProvidersByCity(city, page = 1, limit = 10, filters = {}) {
    try {
      const params = { page, limit, ...filters };
      const response = await api.get(`/providers/city/${city}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by country
   * GET /api/providers/country/:country
   */
  async getProvidersByCountry(country, page = 1, limit = 10, filters = {}) {
    try {
      const params = { page, limit, ...filters };
      const response = await api.get(`/providers/country/${country}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers in Haitian diaspora communities
   * GET /api/providers/diaspora
   */
  async getDiasporaProviders(page = 1, limit = 10, filters = {}) {
    try {
      const params = { page, limit, ...filters };
      const response = await api.get('/providers/diaspora', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by language they speak
   * GET /api/providers/language/:language
   */
  async getProvidersByLanguage(language, page = 1, limit = 10) {
    try {
      const response = await api.get(`/providers/language/${language}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by multiple languages
   * GET /api/providers/languages
   */
  async getProvidersByLanguages(languages, page = 1, limit = 10) {
    try {
      const languagesString = Array.isArray(languages) ? languages.join(',') : languages;
      const response = await api.get('/providers/languages', {
        params: { languages: languagesString, page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get location info
   * GET /api/location-info
   */
  async getLocationInfo(latitude, longitude) {
    try {
      const response = await api.get('/location-info', {
        params: { latitude, longitude }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== SEARCH ROUTES ====================

  /**
   * Advanced search with multiple filters
   * POST /api/service-providers/search/advanced
   */
  async advancedSearch(searchParams) {
    try {
      const response = await api.post('/service-providers/search/advanced', searchParams);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fuzzy search providers
   * GET /api/service-providers/search/fuzzy
   */
  async fuzzySearch(query, options = {}) {
    try {
      const params = { q: query, ...options };
      const response = await api.get('/service-providers/search/fuzzy', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get search filters
   * GET /api/service-providers/search/filters
   */
  async getSearchFilters() {
    try {
      const response = await api.get('/service-providers/search/filters');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Search by availability
   * GET /api/service-providers/search/availability
   */
  async searchByAvailability(day, time, filters = {}) {
    try {
      const params = { day, time, ...filters };
      const response = await api.get('/service-providers/search/availability', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get popular providers
   * GET /api/service-providers/search/popular
   */
  async getPopularProviders(limit = 10, filters = {}) {
    try {
      const params = { limit, ...filters };
      const response = await api.get('/service-providers/search/popular', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by rating range
   * GET /api/service-providers/search/rating-range
   */
  async getProvidersByRatingRange(minRating, maxRating = 5, page = 1, limit = 20) {
    try {
      const params = { minRating, maxRating, page, limit };
      const response = await api.get('/service-providers/search/rating-range', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by price range
   * GET /api/service-providers/search/price-range
   */
  async getProvidersByPriceRange(minPrice, maxPrice, page = 1, limit = 20) {
    try {
      const params = { minPrice, maxPrice, page, limit };
      const response = await api.get('/service-providers/search/price-range', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Autocomplete search
   * GET /api/service-providers/search/autocomplete
   */
  async autocomplete(query, type = 'service') {
    try {
      const params = { q: query, type };
      const response = await api.get('/service-providers/search/autocomplete', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get random providers
   * GET /api/service-providers/search/random
   */
  async getRandomProvidersList(limit = 10, lat = null, lng = null, unit = 'miles') {
    try {
      const params = { limit };
      if (lat && lng) {
        params.lat = lat;
        params.lng = lng;
        params.unit = unit;
      }
      const response = await api.get('/service-providers/search/random', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get random providers by service type
   * GET /api/service-providers/search/random/:serviceType
   */
  async getRandomProvidersByServiceType(serviceType, limit = 10) {
    try {
      const params = { limit };
      const response = await api.get(`/service-providers/search/random/${serviceType}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get random featured providers
   * GET /api/service-providers/search/random-featured
   */
  async getRandomFeaturedProviders(limit = 5) {
    try {
      const params = { limit };
      const response = await api.get('/service-providers/search/random-featured', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROVIDER ROUTES ====================
  // Routes that require provider authentication

  /**
   * Create a new service provider profile (provider registration)
   * POST /api/create-providers
   */
  async createProvider(data) {
    try {
      const response = await api.post('/create-providers', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error); 
    }
  }

  /**
   * Update own service provider profile
   * PUT /api/providers/:id
   */
  async updateProvider(id, data) {
    try {
      const response = await api.put(`/providers/${id}`, data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update own provider activity timestamp
   * POST /api/providers/:id/activity
   */
  async updateProviderActivity(id) {
    try {
      const response = await api.post(`/providers/${id}/activity`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get current user's provider profile
   * GET /api/me
   */
  async getMyProvider() {
    try {
      const response = await api.get('/me');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update current user's provider profile
   * PUT /api/me
   */
  async updateMyProvider(data) {
    try {
      const response = await api.put('/me', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete provider profile
   * DELETE /api/providers/:id
   */
  async deleteProvider(id) {
    try {
      const response = await api.delete(`/providers/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle featured status
   * PATCH /api/providers/:id/featured
   */
  async toggleFeatured(id, isFeatured, duration = 30) {
    try {
      const response = await api.patch(`/providers/${id}/featured`, { isFeatured, duration });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider statistics
   * GET /api/providers/:id/statistics
   */
  async getProviderStatistics(id) {
    try {
      const response = await api.get(`/providers/${id}/statistics`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return {
        success: false,
        statusCode: error.response.status,
        message: error.response.data?.message || error.response.data?.error || 'An error occurred',
        data: error.response.data,
      };
    } else if (error.request) {
      // The request was made but no response was received
      return {
        success: false,
        message: 'No response from server. Please check your connection.',
        error: error.request,
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        error: error,
      };
    }
  }

  /**
   * Get service types (helper for filtering)
   */
  getServiceTypes() {
    return [
      'Electrician', 'Plumber', 'Mason', 'Painter', 'Carpenter', 
      'Tiler', 'Flooring Specialist', 'Handyman', 'HVAC Technician',
      'Solar Installer', 'Barber', 'Hairdresser', 'Makeup Artist',
      'Nail Technician', 'Spa & Massage Therapist', 'Mechanic',
      'Auto Body Repair', 'Car Wash & Detailing', 'Interior Designer',
      'Landscaper', 'Cleaner', 'Pest Control', 'Pool Maintenance',
      'Locksmith', 'Appliance Repair', 'Restaurant', 'Catering',
      'Food Delivery', 'Food Truck', 'Bakery', 'Pastry Shop',
      'Coffee Shop', 'Juice Bar', 'Ice Cream Parlor', 'Food Caterer',
      'Meal Prep Service', 'Culinary Classes', 'Photographer',
      'Videographer', 'Event Planner', 'Wedding Planner', 'Party Rentals',
      'DJ & Entertainment', 'Tutor', 'Language Instructor',
      'Fitness Trainer', 'Yoga Instructor', 'Childcare', 'Elder Care',
      'Pet Grooming', 'Pet Sitting', 'Laundry Service', 'Tailor',
      'Shoe Repair', 'Jewelry Repair', 'IT Support', 'Web Developer',
      'Graphic Designer', 'Digital Marketing', 'Consultant',
      'Notary Public', 'Translation Services', 'Tour Guide',
      'Transportation', 'Delivery Service', 'Moving Company',
      'Storage Services', 'Other'
    ];
  } 

  /**
   * Get supported languages (helper for filtering)
   */
  getSupportedLanguages() {
    return ['Creole', 'English', 'French', 'Spanish'];
  }

  /**
   * Format provider data for display
   */
  formatProvider(provider) {
    if (!provider) return null;

    return {
      ...provider,
      displayName: provider.businessName || provider.name || 'Unnamed Provider',
      ratingFormatted: provider.rating ? provider.rating.toFixed(1) : 'N/A',
      reviewCount: provider.totalReviews || 0,
      locationDisplay: provider.address?.city 
        ? `${provider.address.city}${provider.address.country ? `, ${provider.address.country}` : ''}`
        : 'Location not specified',
    };
  }
}

export default new ProviderService();